import React, {useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useState} from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {ScrollToPlugin} from 'gsap/ScrollToPlugin';
import MainSection from 'src/pages/section/MainSection';
import AlarmSection from 'src/pages/section/AlarmSection';
import FeedSection from 'src/pages/section/FeedSection';
import PointSection from 'src/pages/section/PointSection';
import PartnerSection from 'src/pages/section/PartnerSection';
import Footer from "src/components/layout/Footer";
import LNB from "src/components/common/LNB";
import EventSection from "src/pages/section/EventSection";
import {mediaTablet} from "src/styles/mediaQuery";
import {Helmet, HelmetProvider} from "react-helmet-async";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

interface MainPageViewProps {
    currentSection: string;
    onSectionChange: (section: string) => void;
}

const MainPageView = forwardRef<{ scrollToSection: (section: string) => void }, MainPageViewProps>(
    ({ currentSection, onSectionChange }, ref) => {
        const sectionsRef = useRef<HTMLDivElement[]>([]);
        const containerRef = useRef<HTMLDivElement>(null);
        const [$isLastSection, setIsLastSection] = useState(false);
        const touchStartY = useRef(0);

        const isScrolling = useRef(false);
        const isNormalScroll = useRef(false);
        const scrollY = useRef(0);
        const viewportHeight = useRef(0);
        const lastScrollTime = useRef(Date.now());
        const SCROLL_DELAY = 500;
        const sectionNames = ['mainSection', 'eventSection', 'alarmSection', 'feedSection', 'pointSection', 'partnerSection'];


        useEffect(() => {
            viewportHeight.current = window.innerHeight;
        }, []);

        const scrollAnimation = useCallback((targetY: number, onScrollPrev?: () => void) => {
            const container = containerRef.current;
            if (!container || isScrolling.current || targetY < 0) return;

            const currentTime = Date.now();
            if (currentTime - lastScrollTime.current < SCROLL_DELAY) return;

            onScrollPrev && onScrollPrev()
            isScrolling.current = true;
            lastScrollTime.current = currentTime;

            gsap.to(container, {
                duration: 1,
                scrollTo: {y: targetY, autoKill: false},
                ease: "power2.inOut",
                onComplete: () => {
                    setTimeout(() => {
                        isScrolling.current = false;
                    }, SCROLL_DELAY);
                }
            });
        }, []);

        const wheelHandler = (e: WheelEvent) => {
            const container = containerRef.current;
            if (!container) return;

            const currentTime = Date.now();
            if (currentTime - lastScrollTime.current < SCROLL_DELAY) {
                e.preventDefault();
                return;
            }

            const direction = e.deltaY > 0 ? 1 : -1;
            scrollY.current = container.scrollTop;

            if (currentSection === 'partnerSection') {
                const partnerSection = sectionsRef.current[5];
                const pointSection = sectionsRef.current[4];

                if (direction < 0 && scrollY.current <= partnerSection.offsetTop) {
                    isNormalScroll.current = false;

                    setTimeout(() => {
                        scrollAnimation(pointSection.offsetTop);
                        onSectionChange('pointSection');
                    }, 1000);

                    return;
                }

                isNormalScroll.current = true;
                container.style.overflowY = 'auto';
                return true;
            }

            if (!isScrolling.current) {
                isNormalScroll.current = false;
                container.style.overflowY = 'hidden';

                const nextIndex = Math.max(0, Math.min(
                    sectionsRef.current.length - 1,
                    sectionNames.indexOf(currentSection) + direction
                ));

                scrollAnimation(sectionsRef.current[nextIndex].offsetTop);
                onSectionChange(sectionNames[nextIndex]);
            }
        };

        const touchStartHandler = (e: TouchEvent) => {
            touchStartY.current = e.touches[0].clientY;
        };

        const touchMoveHandler = (e: TouchEvent) => {
            if (!isNormalScroll.current && currentSection !== 'partnerSection') {
                e.preventDefault();
            }
        };

        const touchEndHandler = (e: TouchEvent) => {
            const container = containerRef.current;
            if (!container || isScrolling.current) return;

            const currentTime = Date.now();
            if (currentTime - lastScrollTime.current < SCROLL_DELAY) return;

            const touchEndY = e.changedTouches[0].clientY;
            const direction = touchStartY.current > touchEndY ? 1 : -1;
            const deltaY = Math.abs(touchStartY.current - touchEndY);

            if (deltaY < 30) return;


            if (currentSection === 'partnerSection') {
                const partnerSection = sectionsRef.current[5];
                const pointSection = sectionsRef.current[4];
                scrollY.current = container.scrollTop;

                if (direction < 0 && scrollY.current <= partnerSection.offsetTop) {
                    isNormalScroll.current = false;
                    scrollAnimation(pointSection.offsetTop);
                    onSectionChange('pointSection');
                    return;
                }

                isNormalScroll.current = true;
                container.style.overflowY = 'auto';
                return;
            }

            if (!isScrolling.current) {
                isNormalScroll.current = false;
                container.style.overflowY = 'hidden';

                const nextIndex = Math.max(0, Math.min(
                    sectionsRef.current.length - 1,
                    sectionNames.indexOf(currentSection) + direction
                ));

                scrollAnimation(sectionsRef.current[nextIndex].offsetTop);
                onSectionChange(sectionNames[nextIndex]);
            }
        };

        useEffect(() => {
            const container = containerRef.current;
            if (container) {
                container.addEventListener('wheel', wheelHandler, { passive: false });
                container.addEventListener('touchstart', touchStartHandler);
                container.addEventListener('touchmove', touchMoveHandler, { passive: false });
                container.addEventListener('touchend', touchEndHandler);
            }

            return () => {
                if (container) {
                    container.removeEventListener('wheel', wheelHandler);
                    container.removeEventListener('touchstart', touchStartHandler);
                    container.removeEventListener('touchmove', touchMoveHandler);
                    container.removeEventListener('touchend', touchEndHandler);
                }
            };
        }, [currentSection, onSectionChange, scrollAnimation]);

        useEffect(() => {
            const container = containerRef.current;
            let scrollTriggerInstance: ScrollTrigger | null = null;

            if (container) {
                scrollTriggerInstance = ScrollTrigger.create({
                    trigger: sectionsRef.current[5],
                    scroller: container,
                    start: 'top top',
                    end: 'bottom bottom',
                    onEnter: () => {
                        if (!isNormalScroll.current) {
                            isNormalScroll.current = true;
                            container.style.overflowY = 'auto';
                            setIsLastSection(true);
                        }
                    },
                    onLeaveBack: () => {
                        if (isNormalScroll.current) {
                            isNormalScroll.current = false;
                            container.style.overflowY = 'hidden';
                            setIsLastSection(false);
                        }
                    }
                });
            }
            return () => {
                if (scrollTriggerInstance) {
                    scrollTriggerInstance.kill();
                }
            };
        }, [isNormalScroll]);


        useImperativeHandle(ref, () => ({
            scrollToSection: (section: string) => {
                if (currentSection === section) return

                const index = sectionNames.indexOf(section);
                if (index !== -1 && sectionsRef.current[index]) {
                    const targetOffsetTop = sectionsRef.current[index].offsetTop;
                    scrollAnimation(targetOffsetTop, () => onSectionChange(section));
                    isScrolling.current = false;
                }
            }
        }), [scrollAnimation, onSectionChange]);

        useEffect(() => {
            const robotsMeta = document.querySelector('meta[name="robots"]');
            if (robotsMeta) {
                robotsMeta.remove();
            }
        }, []);

        return (
            <>
                <MainWrap ref={containerRef} $isLastSection={$isLastSection}>
                    <HelmetProvider>
                        <Helmet>
                            <meta name="robots" content="index" />
                        </Helmet>
                    </HelmetProvider>
                    {currentSection !== 'mainSection' && (
                        <LNB
                            currentSection={sectionNames.indexOf(currentSection) - 1}
                            totalSections={sectionsRef.current.length - 1}
                        />
                    )}
                    <FullScreen ref={el => sectionsRef.current[0] = el!} className="section section01">
                        <MainSection />
                    </FullScreen>
                    <FullScreen ref={el => sectionsRef.current[1] = el!} className="section section02">
                        <EventSection />
                    </FullScreen>
                    <FullScreen ref={el => sectionsRef.current[2] = el!} className="section section03">
                        <AlarmSection />
                    </FullScreen>
                    <FullScreen ref={el => sectionsRef.current[3] = el!} className="section section04">
                        <FeedSection />
                    </FullScreen>
                    <FullScreen ref={el => sectionsRef.current[4] = el!} className="section section05">
                        <PointSection />
                    </FullScreen>
                    <FullScreen ref={el => sectionsRef.current[5] = el!} className="section section06">
                        <PartnerSection/>
                        <Footer/>
                    </FullScreen>
                </MainWrap>
            </>

        );
    });

export default MainPageView;

const MainWrap = styled.div<{ $isLastSection: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  height: 100dvh; 
  overflow: ${({ $isLastSection }) => ($isLastSection ? 'auto' : 'hidden')};
`;

const FullScreen = styled.div`
  height: 100dvh;
  &.section06 {
    min-height: 100dvh;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    ${mediaTablet}{
      display: block;
    }
  }
`;